import { useEffect, useRef } from 'react';
import type { CompetitionRound } from '../types/competition-round';

/** How often a competition's live pages ask the server again, unless it says otherwise. */
export const AUTO_REFRESH_INTERVAL = 60_000;

/**
 * The steps the organiser picks from, in seconds; 0 is never. The server's
 * Competition::ROUND_REFRESH_CHOICES, which refuses anything else.
 */
export const ROUND_REFRESH_CHOICES = [0, 10, 15, 30, 60, 90, 120] as const;

/**
 * How a page open on `round` refreshes: at the competition's interval, which
 * comes with the round (`refresh_seconds`), so the organiser changing it is
 * picked up by the next refresh and the clock restarts at the new pace. Never
 * when it is off, and never on a closed round, which nobody writes on any more.
 *
 * Off is final for an open page: it no longer asks, so it only learns the
 * refresh was turned back on when it is reloaded.
 */
export function roundRefreshOptions(
  round: Pick<CompetitionRound, 'refresh_seconds' | 'closed'> | null | undefined,
  enabled = true
): { enabled: boolean; intervalMs: number } {
  const seconds = round?.refresh_seconds ?? AUTO_REFRESH_INTERVAL / 1000;
  return {
    enabled: enabled && !!round && !round.closed && seconds > 0,
    intervalMs: seconds > 0 ? seconds * 1000 : AUTO_REFRESH_INTERVAL,
  };
}

/**
 * Calls `refresh` every `intervalMs` while `enabled` — the live pages of a
 * competition round, which other people write on while this one is open.
 *
 * - **Only while the page is looked at.** A phone in a pocket, or a tab behind
 *   another, asks nothing; coming back to it asks at once if a turn was missed,
 *   so what is on screen is never older than one interval plus the request.
 * - **One at a time.** A slow answer is not overtaken by the next tick: that
 *   tick is skipped instead.
 * - **The latest `refresh`.** Kept in a ref, so a page may pass a fresh closure
 *   on every render without restarting the clock.
 *
 * The first load is the page's own business: this only repeats it.
 */
export function useAutoRefresh(
  refresh: () => unknown,
  { enabled = true, intervalMs = AUTO_REFRESH_INTERVAL }: { enabled?: boolean; intervalMs?: number } = {}
): void {
  const refreshRef = useRef(refresh);
  refreshRef.current = refresh;

  useEffect(() => {
    if (!enabled) return;

    let inFlight = false;
    let lastRun = Date.now();
    let timer: number | undefined;

    const run = async () => {
      if (inFlight) return;
      inFlight = true;
      lastRun = Date.now();
      try {
        await refreshRef.current();
      } catch {
        // A refresh that fails leaves the page as it was: the next one tries again.
      } finally {
        inFlight = false;
      }
    };

    const start = () => {
      if (timer === undefined) timer = window.setInterval(run, intervalMs);
    };
    const stop = () => {
      window.clearInterval(timer);
      timer = undefined;
    };

    const onVisibility = () => {
      if (document.visibilityState === 'visible') {
        if (Date.now() - lastRun >= intervalMs) void run();
        start();
      } else {
        stop();
      }
    };

    if (document.visibilityState !== 'hidden') start();
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      stop();
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [enabled, intervalMs]);
}
